import React, { useState, useEffect, useRef } from "react";
import { Empty } from "antd";
import * as echarts from "echarts";
import { formatCurrency, formatCurrency2 } from "../../../utils/Utils";
import MaterialOverrunDrawer from "./MaterialOverrunDrawer";

const categories = [
  { categoryID: "cat001", categoryName: "Steel" },
  { categoryID: "cat002", categoryName: "Cement" },
  { categoryID: "cat003", categoryName: "Electricals" },
  { categoryID: "cat004", categoryName: "HVAC" },
];

const MaterialWiseSpendAnalysis: React.FC<any> = ({
  materialWiseSpendAnalysisData,
}) => {
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<echarts.ECharts | null>(null);

  const hasData =
    Array.isArray(materialWiseSpendAnalysisData) &&
    materialWiseSpendAnalysisData.length > 0;

  const disposeChart = () => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }
  };

  const initializeChart = (selectedData: any[]) => {
    if (chartContainerRef.current) {
      // Detect a stale instance bound to a detached/different DOM node.
      if (
        chartInstanceRef.current &&
        chartInstanceRef.current.getDom() !== chartContainerRef.current
      ) {
        chartInstanceRef.current.dispose();
        chartInstanceRef.current = null;
      }

      if (!chartInstanceRef.current) {
        chartInstanceRef.current = echarts.init(chartContainerRef.current);
      }

      const option = {
        tooltip: {
          trigger: "axis",
          formatter: (params: any) => {
            const budgetAllocated = formatCurrency(params[0].data.value, "INR");
            const amountSpent = formatCurrency(params[1].data.value, "INR");
            const variationColor =
              params[1].data.indicator === "up" ? "red" : "green";
            return `
                            <div>
                                <p><strong>${params[0].name}</strong></p>
                                <p>Budget Allocated: ${budgetAllocated}</p>
                                <p>Amount Spent: ${amountSpent}</p>
                                <p style="color: ${variationColor};">
                                    Variation: ${params[1].data.variation}% (${
              params[1].data.indicator === "up" ? "Over Spent" : "Under Spent"
            })
                                </p>
                            </div>
                        `;
          },
        },
        grid: {
          left: "3%",
          right: "0",
          bottom: "15%", // Increase this value to add more space for the legend
          top: "8%",
          containLabel: true,
        },
        legend: {
          data: ["Budget Allocated", "Amount Spent"],
          orient: "horizontal",
          bottom: "5%", // Adjust this to create space between the legend and axis labels
          right: "0",
        },
        xAxis: {
          type: "category",
          data: selectedData.map((item) => item.materialName),
          name: "Material",
          nameLocation: "middle",
          nameTextStyle: {
            fontWeight: "bold",
            fontSize: 14,
            padding: 20,
          },
        },
        yAxis: {
          type: "value",
          name: `Amount  (${"INR"})`,
          nameLocation: "middle",

          axisLabel: {
            formatter: (value: number) => formatCurrency2(value, "INR"),
          },
          nameTextStyle: {
            fontWeight: "bold",
            fontSize: 14,
            padding: 50,
          },
        },
        series: [
          {
            name: "Budget Allocated",
            type: "bar",
            data: selectedData.map((item) => ({
              value: item.budgetAllocated,
              indicator: item.indicator,
              variation: item.variation,
            })),
            itemStyle: {
              color: "#3BAEA3",
            },
            barWidth: "20%",
          },
          {
            name: "Amount Spent",
            type: "bar",
            data: selectedData.map((item) => ({
              value: item.spent,
              indicator: item.indicator,
              variation: item.variation,
            })),
            itemStyle: {
              color: "#5470C6",
            },
            barWidth: "20%",
          },
        ],
      };

      chartInstanceRef.current.setOption(option, true);
    }
  };

  useEffect(() => {
    if (hasData) {
      initializeChart(materialWiseSpendAnalysisData);
      // Resize on next frame so the chart adapts to the final committed
      // layout (avoids the "compressed" rendering when the container size
      // settles after the chart is drawn).
      const raf = requestAnimationFrame(() => {
        chartInstanceRef.current?.resize();
      });
      return () => cancelAnimationFrame(raf);
    }
    // Don't dispose when data is empty — keep the instance alive but
    // hidden via visibility so future data re-uses the same instance.
  }, [materialWiseSpendAnalysisData, hasData]);

  // Keep chart sized to its container (window resize, sidebar toggle,
  // parent overlay appearing/disappearing). resize() is deferred to rAF
  // and de-duped to avoid the "ResizeObserver loop completed with
  // undelivered notifications" error that fires when echarts triggers
  // layout from inside the RO callback.
  useEffect(() => {
    let raf = 0;
    const handleResize = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        raf = 0;
        chartInstanceRef.current?.resize();
      });
    };
    window.addEventListener("resize", handleResize);

    let ro: ResizeObserver | null = null;
    if (chartContainerRef.current && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(handleResize);
      ro.observe(chartContainerRef.current);
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      window.removeEventListener("resize", handleResize);
      ro?.disconnect();
    };
  }, []);

  // Dispose only on unmount.
  useEffect(() => {
    return () => {
      disposeChart();
    };
  }, []);

  const [drawerVisible, setDrawerVisible] = useState<boolean>(false);

  const openDrawer = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    e.preventDefault();
    setDrawerVisible(true);
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
  };
  return (
    <div>
      <div
        className="card-title-group mb-1"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div className="card-title">
          <h6 className="title">Material Wise Spend Analysis</h6>
          <p>Insights on budget spends for different materials by category.</p>
        </div>
        {/* <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: 8 }}>Filter by Category:</span>
                    <Select
                        placeholder="Select Category"
                        style={{ width: 200 }}
                        value={selectedCategoryID}
                        onChange={handleCategoryChange}
                    >
                        {categories.map(category => (
                            <Option key={category.categoryID} value={category.categoryID}>
                                {category.categoryName}
                            </Option>
                        ))}
                    </Select>
                </div> */}
      </div>
      {/* Chart container is ALWAYS rendered with a fixed height so echarts
          can measure it correctly. "No data" is overlaid (visibility
          toggle, not display) to avoid orphaning the echarts instance. */}
      <div style={{ position: "relative", minHeight: "300px" }}>
        <div
          ref={chartContainerRef}
          id="budget-overrun-chart"
          style={{
            width: "100%",
            height: "300px",
            visibility: hasData ? "visible" : "hidden",
          }}
        />
        {!hasData && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Empty description="No data available to generate or display the graph." />
          </div>
        )}
      </div>
      <MaterialOverrunDrawer
        visible={drawerVisible}
        onClose={closeDrawer}
        data={materialWiseSpendAnalysisData}
        currency={"INR"}
        categories={categories}
      />
      {hasData && (
        <div style={{ marginTop: 20, fontSize: 16, textAlign: "center" }}>
          <a href="" onClick={openDrawer}>
            Click here to view more details
          </a>
        </div>
      )}
    </div>
  );
};

export default MaterialWiseSpendAnalysis;
